import { Link } from "react-router-dom";
import { SITE } from "@/constants/testIds";
import { API } from "@/lib/api";
import { SECTION_MAP } from "@/lib/sections";
import { RecommendationBadge } from "@/components/site/RecommendationBadge";
import { KiraScoreVisual } from "@/components/site/KiraScoreVisual";

function fileUrl(path) {
  return path ? `${API.replace("/api", "")}${path}` : null;
}

function FooterRight({ report }) {
  if (report.market_cap) {
    return (
      <div className="flex flex-col leading-none items-end">
        <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-kira-textMuted/70">Mkt Cap</span>
        <span className="font-mono text-xs text-white mt-1">{report.market_cap}</span>
      </div>
    );
  }
  if (report.current_price) {
    return <span className="font-mono text-[10px] text-kira-textMuted/70">{report.report_date}</span>;
  }
  return null;
}

/**
 * Premium research card.
 * Top panel: the KIRA Score rendered as a large radial dial on a subtle
 * gradient — replaces the previously-empty dark header. Optional cover image
 * fades in behind at 30% opacity so the score stays readable.
 */
export function ReportCard({ report }) {
  const section = SECTION_MAP[report.section];
  const cover = fileUrl(report.cover_url);
  const logo = fileUrl(report.logo_url);

  return (
    <Link
      data-testid={`${SITE.reportCard}-${report.slug}`}
      to={`/company/${report.slug}`}
      className="group flex flex-col rounded-xl border border-kira-border bg-kira-surface overflow-hidden transition-all duration-300 hover:border-kira-gold/50 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-12px_rgba(212,175,55,0.25)]"
    >
      {/* Score panel */}
      <div className="relative h-44 overflow-hidden border-b border-kira-border">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.18),transparent_60%),linear-gradient(180deg,#161616_0%,#0A0A0A_100%)]" />
        {cover && (
          <img
            src={cover}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-luminosity transition-transform duration-700 group-hover:scale-105"
          />
        )}
        {/* Ticks grid decoration */}
        <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,0.9)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.9)_1px,transparent_1px)] [background-size:20px_20px]" />

        {/* Section label top-left */}
        {section && (
          <p className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.18em] text-kira-textMuted/80">
            {section.short}
          </p>
        )}
        {/* Reco top-right */}
        <div className="absolute top-3 right-3">
          <RecommendationBadge recommendation={report.recommendation} />
        </div>

        {/* Big radial score */}
        <div className="absolute inset-0 flex items-center justify-center">
          <KiraScoreVisual score={report.kira_score} size={110} />
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white/5 border border-kira-border overflow-hidden">
            {logo ? (
              <img src={logo} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="font-chivo font-bold text-sm text-kira-textMuted">
                {report.company_name.charAt(0)}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-chivo font-semibold text-white text-sm leading-tight truncate group-hover:text-kira-gold transition-colors">
              {report.company_name}
            </p>
            <p className="text-[11px] text-kira-textMuted font-mono mt-1 truncate">
              {report.symbol && <span className="text-kira-gold mr-1.5">{report.symbol}</span>}
              {report.sector}
            </p>
          </div>
        </div>

        {report.summary && (
          <p className="mt-3 text-xs text-kira-textMuted line-clamp-2 leading-relaxed">
            {report.summary}
          </p>
        )}

        <div className="mt-auto pt-3 flex items-center justify-between border-t border-kira-border/60 mt-3">
          {report.current_price ? (
            <div className="flex flex-col leading-none">
              <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-kira-textMuted/70">Price</span>
              <span className="font-mono text-xs text-white mt-1">{report.current_price}</span>
            </div>
          ) : (
            <span className="font-mono text-[10px] text-kira-textMuted/70">{report.report_date}</span>
          )}
          <FooterRight report={report} />
        </div>
      </div>
    </Link>
  );
}
